import React, {Fragment, useEffect, useState} from "react";
import {Table, Select, DatePicker, Button, Image, Row, Col, Empty} from "antd";
import SubCompanyInput from "../createDeliveryRequest/deliveryRequestForm/pickupInformation/subCompanyInput/SubCompanyInput";
import {useFetch} from "../../hooks/useFetch";
import {useStore} from "../../hooks/useStore";
import moment from "moment";
import {Link} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {FileSearchOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import locale from "antd/es/date-picker/locale/tr_TR";
import {Box} from "../customer/customerAccountingPage/customerAccountingRow/CustomerAccountingRow.styles";
import UseCreditIcon from "../../public/images/use-credit-icon.svg";
import LoadCreditIcon from "../../public/images/load-credit-icon.svg";
import {ConditionalTooltip} from "../_defaultComponents/ConditionalTooltip/ConditionalTooltip";
import {truncateString} from "../../utils/helpers";
import {ObjectionModal} from "./modals/ObjectionModal";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {TableWrapper} from "../_defaultComponents/TableWrapper/TableWrapper";
import {
    MainContainer,
    LeftSection,
    RightSection,
    HeaderContainer,
    BoxesContainer,
    SubCompanyContainer,
    DateRangeContainer,
    TransactionTypeContainer,
    LoadContainer,
    UseContainer,
    Line
} from "./CustomerCreditLogTable.styles";
import {ColoredLine} from "components/courier/courierDetail/courierDetailStyles";

export const CustomerCreditLogTable = ({
    creditStats,
    selectedDateRange,
    setCreditStats,
    setSelectedDateRange,
    defaultDateRange
}) => {
    const stores = useStore();
    const [selectedTransactionType, setSelectedTransactionType] = useState("all");
    const [selectedSubType, setSelectedSubType] = useState("all");
    const [subCompanyId, setSelectedSubCompanyId] = useState(undefined);
    const [pageParam, setPageParam] = useState(1);
    const [updateFileUpdateVisibilty, setFileUpdateVisibility] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [deliveryId, setDeliveryId] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const [paymentTransactionLogsLoading, paymentTransactionLogs, _, __, refetch] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyPaymentTransactionLogForClient.bind(null, {
            subCompanyId,
            skip: pageParam - 1,
            limit: 10,
            startDate:
                selectedDateRange?.length === 2 && selectedDateRange[0] && selectedDateRange[1]
                    ? moment(selectedDateRange[0]).format("YYYY-MM-DD")
                    : undefined,
            endDate:
                selectedDateRange?.length === 2 && selectedDateRange[1]
                    ? moment(selectedDateRange[1]).format("YYYY-MM-DD")
                    : undefined,
            transactionType:
                selectedTransactionType === "all" ? undefined : selectedTransactionType,
            subType: selectedSubType === "all" ? undefined : selectedSubType
        }),
        initialCall: false,
        showLoading: true,
        dependencies: [
            subCompanyId,
            selectedTransactionType,
            selectedSubType,
            selectedDateRange,
            pageParam
        ]
    });

    const [] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.userStore.getUserCreditStats.bind(null, {
            ...(selectedDateRange?.length === 2 && {
                startDate: moment(selectedDateRange?.[0]).format("YYYY-MM-DD"),
                endDate: moment(selectedDateRange?.[1]).format("YYYY-MM-DD")
            })
        }),
        dependencies: [selectedDateRange],
        initialCall: true,
        callbackFunc: result => {
            setCreditStats && setCreditStats(result);
        }
    });

    const handleSelectedTransactionType = value => {
        setSelectedTransactionType(value);
        setPageParam(1);
    };

    const closeModals = () => {
        setFileUpdateVisibility(false);
    };

    const handleObjectionModalClose = isSubmitted => {
        closeModals();
        if (isSubmitted) {
            setRefreshTrigger(prev => prev + 1);
        }
    };

    const handleObjection = (orderId, deliveryId) => {
        setOrderId(orderId);
        setDeliveryId(deliveryId);
        setFileUpdateVisibility(true);
    };

    useEffect(() => {
        refetch();
    }, []);

    useEffect(() => {
        refetch();
    }, [refreshTrigger]);

    //trigger when users all sub-companies are loaded
    useEffect(() => {
        if (stores.companyStore.subCompanyList.length === 1) {
            setSelectedSubCompanyId(stores.companyStore.subCompanyList[0]._id);
        }
    }, [JSON.stringify(stores.companyStore.subCompanyList)]);

    const renderLoadedCredit = () => {
        if (creditStats === null) {
            return "###";
        }

        return `${creditStats?.data?.data?.stats?.totalCreditLoad?.toLocaleString("tr-TR", {
            maximumFractionDigits: 1
        }) || "-"} ₺`;
    };

    const renderUsedCredit = () => {
        if (creditStats === null) {
            return "###";
        }

        return `${creditStats?.data?.data?.stats?.totalCreditUse?.toLocaleString("tr-TR", {
            maximumFractionDigits: 1
        }) || "-"} ₺`;
    };

    const startingBalance = () => {
        if (creditStats === null) {
            return "###";
        }

        return `${creditStats?.data?.data?.stats?.startingBalance?.toLocaleString("tr-TR", {
            maximumFractionDigits: 1
        }) || "-"} ₺`;
    };

    const endingBalance = () => {
        if (creditStats === null) {
            return "###";
        }

        return `${creditStats?.data?.data?.stats?.endingBalance?.toLocaleString("tr-TR", {
            maximumFractionDigits: 1
        }) || "-"} ₺`;
    };

    return useObserver(() => (
        <div style={{width: "100%"}}>
            <Line />
            <div style={{width: "100%"}}>
                <div>
                    <MainContainer>
                        <LeftSection>
                            <HeaderContainer>
                                <SubCompanyContainer>
                                    <SubCompanyInput
                                        onChange={value => setSelectedSubCompanyId(value)}
                                        initialCall={true}
                                        disabled={false}
                                        value={subCompanyId}
                                    />
                                </SubCompanyContainer>
                                <DateRangeContainer>
                                    <span>Başlangıç - Bitiş Tarihi</span>
                                    <DatePicker.RangePicker
                                        placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                                        onChange={value => {
                                            setSelectedDateRange(value);
                                        }}
                                        value={selectedDateRange}
                                        locale={locale}
                                        allowClear
                                    />
                                </DateRangeContainer>
                                <TransactionTypeContainer>
                                    <span>İşlem Türü</span>
                                    <Select
                                        defaultValue="all"
                                        options={[
                                            {
                                                value: "LOAD",
                                                label: "Yükleme"
                                            },
                                            {
                                                value: "USE",
                                                label: "Harcama"
                                            },
                                            {
                                                value: "CASH",
                                                label: "Nakit Tahsilat"
                                            },
                                            {
                                                value: "all",
                                                label: "Tümü"
                                            }
                                        ]}
                                        onChange={handleSelectedTransactionType}
                                        value={selectedTransactionType}
                                    />
                                </TransactionTypeContainer>
                            </HeaderContainer>
                            <BoxesContainer>
                                <Box>
                                    <img src={LoadCreditIcon} alt="load credit icon" />
                                    <b>Toplam Yüklenen</b>
                                    <b>{renderLoadedCredit()}</b>
                                </Box>
                                <Box>
                                    <img src={UseCreditIcon} alt="use credit icon" />
                                    <b>Toplam Harcanan</b>
                                    <b>{renderUsedCredit()}</b>
                                </Box>
                                {/* {selectedDateRange?.length === 2 && (
                                <Fragment>
                                    <Box>
                                        <img src={LoadCreditIcon} alt="use credit icon" />
                                        <b>Başlangıç Bakiyesi</b>
                                        <b>{startingBalance()}</b>
                                    </Box>
                                    <Box>
                                        <img src={UseCreditIcon} alt="use credit icon" />
                                        <b>Bitiş Bakiyesi</b>
                                        <b>{endingBalance()}</b>
                                    </Box>
                                </Fragment>
                            )} */}
                            </BoxesContainer>
                        </LeftSection>
                        {/* <RightSection>
                        <CustomerAccountingDetailInfo
                            setCreditStats={setCreditStats}
                            selectedDateRange={selectedDateRange}
                        />
                    </RightSection> */}
                    </MainContainer>

                    <div style={{marginTop: "20px"}}>
                        <TableWrapper
                            rowKey={(_, index) => index.toString()}
                            dataSource={paymentTransactionLogs?.data?.rows}
                            loading={paymentTransactionLogsLoading}
                            scroll={{x: "max-content"}}
                            locale={{
                                emptyText: <EmptyMessage />
                            }}
                            pagination={{
                                total: paymentTransactionLogs?.data?.dataCount,
                                current: pageParam,
                                hideOnSinglePage: true,
                                showSizeChanger: false,
                                onChange: page => {
                                    setPageParam(page);
                                }
                            }}
                            expandable={{
                                expandedRowRender: record => {
                                    return (
                                        <>
                                            {record.subTypes && record.subTypes.length > 0 ? (
                                                <Table
                                                    dataSource={record.subTypes}
                                                    pagination={false}
                                                    showHeader={false}
                                                    size="small"
                                                    rowClassName={(record, index) => {
                                                        return "gray-background";
                                                    }}
                                                >
                                                    <Table.Column width={200} />
                                                    <Table.Column width={200} />
                                                    <Table.Column width={200} />
                                                    <Table.Column width={150} />
                                                    <Table.Column
                                                        align="center"
                                                        key="subType"
                                                        width={200}
                                                        dataIndex="subType"
                                                        render={subType => (
                                                            <div
                                                                style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "bold",
                                                                    display: "flex",
                                                                    justifyContent: "flex-start",
                                                                    paddingLeft: 30
                                                                }}
                                                            >
                                                                {(subType === "REFUND" && "İADE") ||
                                                                    (subType === "OBJECTION" &&
                                                                        "İTİRAZ") ||
                                                                    (subType === "CASH" &&
                                                                        "NAKİT TAHSİLAT") ||
                                                                    subType}
                                                            </div>
                                                        )}
                                                    />
                                                    <Table.Column
                                                        key="amount"
                                                        align="center"
                                                        width={150}
                                                        dataIndex="amount"
                                                        render={amount => (
                                                            <div
                                                                style={{
                                                                    fontSize: "12px",
                                                                    fontWeight: "bold",
                                                                    display: "flex"
                                                                }}
                                                            >
                                                                {amount} TL
                                                            </div>
                                                        )}
                                                    />
                                                    <Table.Column width={150} />
                                                    <Table.Column width={150} />
                                                </Table>
                                            ) : record.transactionPhoto ? (
                                                <div>
                                                    <h2>Fatura Görüntüsü </h2>
                                                    {record.transactionPhoto.includes(".pdf") ||
                                                    record.transactionPhoto.includes(
                                                        "buluttahsilat"
                                                    ) ? (
                                                        <a
                                                            href={record.transactionPhoto}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            <Button type="primary">
                                                                Dekont Görüntüle
                                                            </Button>
                                                        </a>
                                                    ) : (
                                                        <Image
                                                            src={record.transactionPhoto}
                                                            width={200}
                                                            height={200}
                                                            style={{
                                                                objectFit: "contain"
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            ) : (
                                                "Fatura Görüntüsü Bulunamadı"
                                            )}
                                        </>
                                    );
                                },
                                rowExpandable: record =>
                                    (record.subTypes && record.subTypes.length > 0) ||
                                    (record?.subType === "PAYMENT" &&
                                        record?.transactionType === "LOAD")
                            }}
                        >
                            <Table.Column
                                dataIndex="createdAt"
                                title={"Tarih"}
                                width={200}
                                render={date => (
                                    <span>{moment(date).format("DD.MM.YYYY HH:mm")}</span>
                                )}
                            />
                            <Table.Column
                                dataIndex="vigoOrderNumber"
                                width={200}
                                title={"Sipariş Numarası"}
                                render={(_, record) => (
                                    <span>
                                        {record?.order !== null ? (
                                            record?.order?.vigoOrderNumber ? (
                                                <Link
                                                    target="_blank"
                                                    data-testid="orderNumber"
                                                    style={{maxWidth: "470px"}}
                                                    to={{
                                                        pathname:
                                                            (stores.meStore.checkRole(
                                                                "isUatPoolCustomer"
                                                            )
                                                                ? "/musteri/siparislerim/"
                                                                : "/orders/") + record?.deliveryId
                                                    }}
                                                >
                                                    {record?.order?.vigoOrderNumber}
                                                </Link>
                                            ) : (
                                                <ConditionalTooltip
                                                    condition={
                                                        record?.order?.orderNumber
                                                            ? truncateString(
                                                                  record?.order?.orderNumber,
                                                                  16
                                                              ).indexOf("...") > 0
                                                            : false
                                                    }
                                                    text={record?.order?.orderNumber}
                                                    child={
                                                        record?.deliveryId &&
                                                        record?.order?.orderNumber ? (
                                                            <Link
                                                                target="_blank"
                                                                data-testid="orderNumber"
                                                                style={{maxWidth: "470px"}}
                                                                to={{
                                                                    pathname:
                                                                        (stores.meStore.checkRole(
                                                                            "isUatPoolCustomer"
                                                                        )
                                                                            ? "/musteri/siparislerim/"
                                                                            : "/orders/") +
                                                                        record?.deliveryId
                                                                }}
                                                            >
                                                                {truncateString(
                                                                    record?.order?.orderNumber,
                                                                    16
                                                                )}
                                                            </Link>
                                                        ) : (
                                                            "-"
                                                        )
                                                    }
                                                />
                                            )
                                        ) : (
                                            <ConditionalTooltip
                                                condition={
                                                    record?.order?.orderNumber
                                                        ? truncateString(
                                                              record?.order?.orderNumber,
                                                              16
                                                          ).indexOf("...") > 0
                                                        : false
                                                }
                                                text={record?.order?.orderNumber}
                                                child={
                                                    record?.deliveryId &&
                                                    record?.order?.orderNumber ? (
                                                        <Link
                                                            target="_blank"
                                                            data-testid="orderNumber"
                                                            style={{maxWidth: "470px"}}
                                                            to={{
                                                                pathname:
                                                                    (stores.meStore.checkRole(
                                                                        "isUatPoolCustomer"
                                                                    )
                                                                        ? "/musteri/siparislerim/"
                                                                        : "/orders/") +
                                                                    record?.deliveryId
                                                            }}
                                                        >
                                                            {truncateString(
                                                                record?.order?.orderNumber,
                                                                16
                                                            )}
                                                        </Link>
                                                    ) : (
                                                        "-"
                                                    )
                                                }
                                            />
                                        )}
                                    </span>
                                )}
                            />
                            {!subCompanyId && (
                                <Table.Column
                                    key="subcompanyId"
                                    dataIndex="subcompanyId"
                                    width={150}
                                    title={"Detay"}
                                    render={subcompanyId => (
                                        <CustomButton
                                            href={"/panel/companies/sub-company/" + subcompanyId}
                                            data-testid="subcompanyDetail"
                                            onClick={event => {
                                                event.preventDefault();
                                                window.location.href =
                                                    "/panel/companies/sub-company/" + subcompanyId;
                                            }}
                                            icon={<FileSearchOutlined />}
                                        />
                                    )}
                                />
                            )}
                            <Table.Column
                                key="courier"
                                dataIndex="courier"
                                width={200}
                                title={"Kurye"}
                                render={courier => (
                                    <span>
                                        {courier ? (
                                            stores.meStore.checkRole("notVisibleForCompanyUser") ? (
                                                <Link
                                                    target="_blank"
                                                    to={{
                                                        pathname: "/couriers/" + courier?.id
                                                    }}
                                                >
                                                    {courier?.fullname}
                                                </Link>
                                            ) : (
                                                courier?.fullname ?? "Kurye"
                                            )
                                        ) : (
                                            "-"
                                        )}
                                    </span>
                                )}
                            />
                            <Table.Column
                                dataIndex="receiverName"
                                title={"Alıcı Adı"}
                                width={150}
                                render={receiverName => (
                                    <span>{receiverName ? receiverName : "-"}</span>
                                )}
                            />
                            <Table.Column
                                dataIndex="transactionType"
                                title={"İşlem Türü"}
                                width={200}
                                render={transactionType => (
                                    <div>
                                        {transactionType === "LOAD" ? (
                                            <LoadContainer>Yükleme</LoadContainer>
                                        ) : transactionType === "CASH" ? (
                                            <LoadContainer>Nakit Tahsilat</LoadContainer>
                                        ) : (
                                            <UseContainer>Harcama</UseContainer>
                                        )}
                                    </div>
                                )}
                            />
                            <Table.Column
                                dataIndex="clientAmount"
                                title={"Fiyat"}
                                width={150}
                                render={clientAmount => (
                                    <span>
                                        {clientAmount
                                            ? `${clientAmount?.toLocaleString("tr-TR", {
                                                  maximumFractionDigits: 1
                                              })} TL`
                                            : "0 TL"}
                                    </span>
                                )}
                            />
                            <Table.Column
                                dataIndex="description"
                                width={150}
                                title={"Açıklama"}
                                render={description => (
                                    <span>{description ? `${description}` : "-"}</span>
                                )}
                            />
                            {selectedTransactionType !== "LOAD" && (
                                <Table.Column
                                    dataIndex="transactionType"
                                    title={"İtiraz Bildir"}
                                    width={150}
                                    render={(_, record) => {
                                        if (
                                            record.transactionType === "USE" &&
                                            !record.paymentObjection
                                        ) {
                                            return (
                                                <CustomButton
                                                    onClick={() =>
                                                        handleObjection(
                                                            record.order?._id,
                                                            record.deliveryId
                                                        )
                                                    }
                                                    style={{borderRadius: "10px"}}
                                                >
                                                    İtiraz Bildir <ExclamationCircleOutlined />
                                                </CustomButton>
                                            );
                                        }
                                        if (record.paymentObjection) {
                                            const {status} = record.paymentObjection;

                                            if (status === "pending") {
                                                return (
                                                    <CustomButton
                                                        style={{
                                                            backgroundColor: "#ff9f43",
                                                            color: "white",
                                                            borderRadius: "10px"
                                                        }}
                                                        disabled
                                                    >
                                                        İnceleniyor
                                                    </CustomButton>
                                                );
                                            }

                                            if (status === "approved") {
                                                return (
                                                    <CustomButton
                                                        style={{
                                                            backgroundColor: "#00b894",
                                                            color: "white",
                                                            borderRadius: "10px"
                                                        }}
                                                        disabled
                                                    >
                                                        Onaylandı
                                                    </CustomButton>
                                                );
                                            }

                                            if (status === "rejected") {
                                                return (
                                                    <CustomButton
                                                        style={{
                                                            backgroundColor: "#ff5354",
                                                            color: "white",
                                                            borderRadius: "10px"
                                                        }}
                                                        disabled
                                                    >
                                                        Reddedildi
                                                    </CustomButton>
                                                );
                                            }
                                        }
                                        return null;
                                    }}
                                />
                            )}
                        </TableWrapper>
                    </div>
                    <ObjectionModal
                        subCompanyId={subCompanyId}
                        deliveryId={deliveryId}
                        orderId={orderId}
                        visibility={updateFileUpdateVisibilty}
                        onCancel={handleObjectionModalClose}
                    />
                </div>
            </div>
        </div>
    ));
};
